// Permanent-delete confirm for inactive manufacturing models (org owner / platform).
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import {
  ghostBtnClass,
  mfgConfirmDialogClass,
  mfgSolidDangerBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

export function ManufacturingModelPermanentDeleteConfirm({
  open,
  recipe,
  busy,
  error,
  blockers,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  recipe: MfgRecipeSummary | null;
  busy: boolean;
  error: string | null;
  blockers: ReadonlyArray<{ code: string; message: string }>;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open || !recipe) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfg-model-permanent-delete-title"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className={`max-w-md ${mfgConfirmDialogClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="mfg-model-permanent-delete-title"
          className="text-base font-semibold text-gray-900 dark:text-zinc-100"
        >
          Permanently delete {recipe.code} · {recipe.name}?
        </h3>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          This removes the inactive model and its BOM lines from your organization. This
          cannot be undone. Deletion is blocked while other deals, units, or BOMs still
          reference this model.
        </p>
        {blockers.length > 0 ? (
          <ul className="list-disc space-y-1 pl-5 text-sm text-amber-800 dark:text-amber-200">
            {blockers.map((b) => (
              <li key={b.code}>{b.message}</li>
            ))}
          </ul>
        ) : null}
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={ghostBtnClass} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={mfgSolidDangerBtnClass}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
